import type { OnboardingRequestType } from "../types";

const OWNER_NAME = {
  key: "ownerName",
  label: "Owner Name",
  kind: "text",
  required: true,
  prefill: "name",
  maxLength: 200,
} as const;

const OWNER_EMAIL = {
  key: "ownerEmail",
  label: "Owner Email",
  kind: "email",
  required: true,
  prefill: "email",
} as const;

const BUSINESS_AREA = {
  key: "businessArea",
  label: "Business Area",
  kind: "business-area",
  required: true,
  description: "Which business area does this application belong to?",
  prefill: "businessArea",
} as const;

const DIVISION = {
  key: "division",
  label: "Division",
  kind: "division",
  required: true,
  description: "Organizational unit within your business area",
  prefill: "division",
} as const;

const COST_CENTER = {
  key: "costCenter",
  label: "Cost Center",
  kind: "text",
  required: false,
  description: "Cost center code for billing and chargeback",
  placeholder: "AS1234",
  maxLength: 100,
} as const;

export const ONBOARDING_REQUEST_TYPES: OnboardingRequestType[] = [
  {
    slug: "k8s-namespace",
    label: "Kubernetes namespace",
    description:
      "Provision a Kubernetes namespace with Azure Container Registry access, Key Vault integration and RBAC configuration.",
    groups: [
      {
        title: "Namespace configuration",
        description: "Define your Kubernetes namespace and basic configuration",
        fields: [
          {
            key: "namespaceName",
            label: "Namespace Name",
            kind: "text",
            required: true,
            description:
              "Format: {descriptive-name}-k8s-deploy (e.g. payment-service-k8s-deploy)",
            pattern: "^[a-z0-9]([-a-z0-9]*[a-z0-9])?-k8s-deploy$",
            maxLength: 63,
            placeholder: "my-application-k8s-deploy",
            help: "Lowercase, alphanumeric and hyphens only, max 63 characters, must end with -k8s-deploy",
            availabilityCheck: "namespace",
          },
          {
            key: "keyVaultStrategy",
            label: "Key Vault Strategy",
            kind: "select",
            required: true,
            description:
              "Choose how you want to manage secrets for this namespace",
            default: "platform",
            options: [
              {
                value: "platform",
                label:
                  "Platform Key Vault (Recommended) - We provision and manage a dedicated Key Vault",
              },
              {
                value: "private",
                label:
                  "Private Key Vault - I will use my own existing Azure Key Vault",
              },
            ],
          },
        ],
      },
      {
        title: "GitHub repository access",
        description:
          "Configure which GitHub repositories can push container images to Azure Container Registry (optional)",
        fields: [
          {
            key: "repositories",
            label: "GitHub Repositories",
            kind: "list",
            required: false,
            description:
              "Repositories that need ACR push access. Format: organization/repository",
            pattern: "^[a-zA-Z0-9_-]+/[a-zA-Z0-9_-]+$",
            placeholder: "Sandvik-Common/my-application",
          },
        ],
      },
      {
        title: "Ownership & contact information",
        description: "Define the primary owner and contact for this namespace",
        fields: [
          {
            ...OWNER_NAME,
            description:
              "Primary owner/contact for this namespace (will receive admin access)",
          },
          {
            ...OWNER_EMAIL,
            description: "Email address of the namespace owner",
          },
          {
            key: "ownerRole",
            label: "Owner Role",
            kind: "text",
            required: true,
            description: "Role/title of the namespace owner",
            placeholder: "DevOps Engineer",
            maxLength: 200,
          },
        ],
      },
      {
        title: "Organizational metadata",
        description:
          "Provide organizational information for resource tagging and tracking",
        fields: [
          {
            key: "teamName",
            label: "Team Name",
            kind: "text",
            required: true,
            description: "Your team name (used for resource tagging)",
            placeholder: "Platform Engineering",
            maxLength: 200,
          },
          BUSINESS_AREA,
          DIVISION,
          COST_CENTER,
          {
            key: "applicationId",
            label: "Application ID",
            kind: "text",
            required: false,
            description:
              "Application ID from APM system (ServiceNow, Alfapet, etc.)",
            placeholder: "APP-12345",
            maxLength: 100,
          },
        ],
      },
    ],
  },
  {
    slug: "dependency-track",
    label: "DependencyTrack",
    description:
      "Provision DependencyTrack for software supply chain security and vulnerability management.",
    groups: [
      {
        title: "Ownership & contact information",
        description:
          "Define the primary owner and contact for DependencyTrack access",
        fields: [
          {
            ...OWNER_NAME,
            label: "Owner",
            description: "Primary owner/contact for DependencyTrack access",
          },
          { ...OWNER_EMAIL, description: "Email address of the owner" },
          COST_CENTER,
          BUSINESS_AREA,
          { ...DIVISION, label: "Division / Business Unit" },
          {
            key: "teamName",
            label: "Team Name",
            kind: "text",
            required: false,
            description:
              "Your team name (used for resource tagging and organization)",
            placeholder: "Platform Engineering",
            maxLength: 200,
          },
        ],
      },
      {
        title: "Project information",
        description:
          "Provide details about your projects and development environment",
        fields: [
          {
            key: "projectCount",
            label: "How many projects do you want to track?",
            kind: "number",
            required: false,
            description:
              "Estimated number of software projects/components you plan to track",
            placeholder: "5",
            minimum: 1,
          },
          {
            key: "programmingLanguages",
            label: "Programming Languages",
            kind: "textarea",
            required: true,
            description: "Main programming languages used in your projects",
            placeholder: "e.g., Java, Python, JavaScript, Go",
            maxLength: 1000,
          },
          {
            key: "developerPlatform",
            label: "Developer Platform",
            kind: "select",
            required: false,
            description: "Source code management platform",
            default: "Not specified",
            options: [
              "GitHub",
              "GitLab",
              "Gerrit",
              "Other",
              "Not specified",
            ].map((value) => ({ value, label: value })),
          },
          {
            key: "cicdPlatform",
            label: "CI/CD Platform",
            kind: "select",
            required: false,
            description:
              "Continuous Integration/Continuous Deployment platform",
            default: "Not specified",
            options: [
              "Jenkins",
              "GitHub Actions",
              "GitLab CI",
              "Azure DevOps",
              "Other",
              "Not specified",
            ].map((value) => ({ value, label: value })),
          },
        ],
      },
    ],
  },
];

export function onboardingTypeBySlug(
  slug: string,
): OnboardingRequestType | undefined {
  return ONBOARDING_REQUEST_TYPES.find((t) => t.slug === slug);
}
